import { useSupabase } from "~/composables/useSupabase";
import type { BusinessUnit } from "@asap/shared/schemas/business-unit";
import { BOARD_MANAGERS_EMAILS } from "@asap/shared/utils/constants";
import { authUserColumns, teamMemberColumns, workspaceColumns } from "@asap/shared/utils/supabase.columns";
import type { AppRole } from "@asap/shared/utils/supabase.enum";
import type { AuthUser, TeamMember, Workspace } from "@asap/shared/utils/supabase.types";
import { createSharedComposable } from "@vueuse/core";
import { useToast } from "~/composables/useToast";
import { useAsapWorkspace } from "~/components/workspace/composables/useWorkspace";
import { useGleap } from "~/composables/useGleap";

export const useUserMe = createSharedComposable(function () {
  const authUser = ref<AuthUser>();
  const userRoles = ref<AppRole[]>([]);
  const teamMember = ref<TeamMember>();
  const businessUnit = ref<BusinessUnit>();

  const workspaces = ref<Workspace[]>([]);
  const mainWorkspace = ref<Workspace>();
  const currentWorkspace = ref<Workspace>();
  const invitedWorkspaces = ref<Workspace[]>();

  const revieweeWorkspaces = ref<string[]>([]);
  const isBoardManager = ref(false);
  const supabaseAuthUser = useSupabaseUser();
  const supabase = useSupabase();

  const { addToastWarning } = useToast();
  const { asapWorkspaceId } = useAsapWorkspace();

  // Keyed on primitives: @nuxtjs/supabase replaces the user claims object on every navigation.
  watch(
    [
      () => supabaseAuthUser.value?.sub,
      () => teamMember.value?.team_member_workspaces.map((membership) => membership.workspace_id).join(","),
      () => teamMember.value?.main_workspace_id,
      () => teamMember.value?.workspace_id,
    ],
    loadWorkspaces,
    { immediate: true }
  );

  async function loadWorkspaces() {
    if (!supabaseAuthUser.value || !teamMember.value) return;

    const workspaceIds = teamMember.value.team_member_workspaces.map((membership) => membership.workspace_id);
    if (workspaceIds.length === 0) return;

    const { data, error } = await supabase.from("workspace").select(workspaceColumns).in("id", workspaceIds);

    if (error) throw error;

    workspaces.value = data;
    mainWorkspace.value = data.find((workspace) => workspace.id === teamMember.value?.main_workspace_id);
    currentWorkspace.value = data.find((workspace) => workspace.id === teamMember.value?.workspace_id);
    invitedWorkspaces.value = data.filter((workspace) => workspace.id !== teamMember.value?.main_workspace_id);
  }

  watch([() => teamMember.value?.id, asapWorkspaceId], loadRevieweeWorkspaces, { immediate: true });

  async function loadRevieweeWorkspaces() {
    if (!teamMember.value || !asapWorkspaceId.value) return;

    const { data, error } = await supabase
      .from("team_member")
      .select("main_workspace_id")
      .neq("main_workspace_id", asapWorkspaceId.value)
      .eq("manager_id", teamMember.value.id);

    if (error) throw error;

    revieweeWorkspaces.value = data.map((teamMember) => teamMember.main_workspace_id);
  }

  let identityLoad: { authUserId: string; promise: Promise<void> } | undefined;

  const loadIdentity = async (authUserId: string) => {
    const [authUserResult, userRoleResult, teamMemberResult] = await Promise.all([
      supabase.from("auth_user").select(authUserColumns).eq("id", authUserId).single(),
      supabase.from("user_role").select("role").eq("auth_user_id", authUserId),
      supabase.from("team_member").select(teamMemberColumns).eq("auth_user_id", authUserId).maybeSingle(),
    ]);

    if (supabaseAuthUser.value?.sub !== authUserId) return;

    if (authUserResult.data) authUser.value = authUserResult.data;
    if (userRoleResult.data) userRoles.value = userRoleResult.data.map((userRole) => userRole.role);

    if (teamMemberResult.error) throw teamMemberResult.error;

    if (teamMemberResult.data) {
      teamMember.value = teamMemberResult.data;
      if (teamMemberResult.data.business_unit) {
        businessUnit.value = teamMemberResult.data.business_unit;
      }
    }

    isBoardManager.value = BOARD_MANAGERS_EMAILS.includes(authUser.value?.email ?? "");
  };

  const refetch = async function () {
    const authUserId = supabaseAuthUser.value?.sub;
    if (!authUserId) return;

    if (identityLoad?.authUserId === authUserId) return identityLoad.promise;

    const promise = loadIdentity(authUserId).finally(() => {
      if (identityLoad?.promise === promise) identityLoad = undefined;
    });
    identityLoad = { authUserId, promise };
    return promise;
  };

  watch(() => supabaseAuthUser.value?.sub, refetch, { immediate: true });

  const workspaceId = computed(() => teamMember.value?.workspace_id);

  const isAccounting = computed(() => userRoles.value.includes("accounting_team_member"));
  const isConsultant = computed(() => userRoles.value.includes("consultant_team_member"));
  const isConsultantOnly = computed(
    () => userRoles.value.includes("consultant_team_member") && userRoles.value.length === 2
  );
  const isCSM = computed(() => userRoles.value.includes("csm_team_member"));
  const isExternalCsm = computed(
    () => userRoles.value.includes("external_csm_team_member") && userRoles.value.includes("csm_team_member")
  );
  const isMarketing = computed(() => userRoles.value.includes("team_member_marketing"));
  const isDeveloper = computed(() => userRoles.value.includes("team_member_developer"));
  const isIndependentAdministrator = computed(() => userRoles.value.includes("team_member_independent_administrator"));
  const isSupportRole = computed(() => isAccounting.value || isCSM.value || isMarketing.value || isExternalCsm.value);
  const isManager = computed(() => teamMember.value?.management_level && teamMember.value?.management_level > 1);

  const isAutonomous = computed(
    () =>
      (teamMember.value?.management_level && teamMember.value?.management_level > 1) ||
      !!teamMember.value?.is_autonomous
  );
  const isIndependent = computed(() => !!teamMember.value?.is_independent);

  const switchWorkspace = async function (workspace: Workspace) {
    if (!teamMember.value) return;

    const { data, error } = await supabase
      .from("team_member")
      .update({ workspace_id: workspace.id })
      .eq("id", teamMember.value.id)
      .select<string, TeamMember>(teamMemberColumns)
      .single();

    if (error) throw error;

    teamMember.value = data;
  };

  const addRoles = async function (roles: AppRole[], authUserId: string) {
    const rolesToAdd = roles.map((role) => {
      return {
        auth_user_id: authUserId,
        role,
      };
    });
    const { error } = await supabase.from("user_role").insert(rolesToAdd);
    if (error) throw new Error(error.message);
  };

  const deleteRoles = async function (roles: AppRole[], authUserId: string) {
    if (roles.includes("team_member_developer")) {
      addToastWarning({
        title:
          "Vous êtes en train de supprimer un rôle essentiel. Pour le rajouter à nouveau, il faudra le faire directement en DB car vous n'aurez plus accès à cette fonctionnalité depuis l'application.",
      });
    }
    const { error } = await supabase.from("user_role").delete().in("role", roles).eq("auth_user_id", authUserId);
    if (error) throw new Error(error.message);
  };

  watchEffect(() => {
    if (!supabaseAuthUser.value) return;

    if (!teamMember.value) return;
    if (!businessUnit.value) return;
    if (!teamMember.value.email) return;

    const id = teamMember.value.id;

    const { first_name, last_name } = teamMember.value.auth_user ?? {};
    const name = `${first_name} ${last_name}`;

    const { identify } = useGleap();

    identify(id, {
      name,
      email: supabaseAuthUser.value.email,
      customData: {
        businessUnit: businessUnit.value.name,
        roles: userRoles.value.join(","),
        onboarded: teamMember.value.is_onboarded,
      },
    });
  });

  return {
    authUser,
    teamMember,
    userRoles,
    workspaceId,
    businessUnit,
    isAccounting,
    isBoardManager,
    isIndependentAdministrator,
    isConsultant,
    isConsultantOnly,
    isExternalCsm,
    isCSM,
    isMarketing,
    isDeveloper,
    isSupportRole,
    isIndependent,
    isAutonomous,
    isManager,
    workspaces,
    currentWorkspace,
    mainWorkspace,
    revieweeWorkspaces,
    invitedWorkspaces,
    addRoles,
    deleteRoles,
    refetch,
    switchWorkspace,
  };
});
